import { Alert, Progress, Tooltip, UploadFile } from 'antd';
import React, { useState } from 'react';
import { formatFileSize, getFileIcon } from 'utils/format';
import {
  DeleteOutlined,
  ReloadOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { CustomFile } from 'components/features/file-uploader/types';
import { EFileStatus } from 'components/features/file-uploader/enum';
import { isNumber } from 'lodash';

interface FileItemProps {
  file: UploadFile;
  handleDownload: (file: UploadFile) => Promise<void>;
  uploading: { [key: string]: boolean };
  simulateUpload: (file: CustomFile) => Promise<void>;
  handleRemove: (file: CustomFile) => void;
  disabled?: boolean;
}

const FileItem: React.FC<FileItemProps> = ({
  file,
  handleDownload,
  uploading,
  simulateUpload,
  handleRemove,
  disabled = false,
}) => {
  const [showNameToolTip, setShowNameToolTip] = useState(false);
  return (
    <div className="flex items-center justify-between p-4 border-b last:border-b-0">
      <div className="flex items-center space-x-4 flex-grow">
        <img className="w-10" src={getFileIcon(file.name)} alt={file.name} />
        <div className="flex-grow">
          <div className="flex items-center space-x-2">
            <Tooltip
              title={file.name}
              open={showNameToolTip}
              placement="bottom"
            >
              <span
                className="font-medium truncate max-w-[65%] cursor-pointer"
                onMouseEnter={() => setShowNameToolTip(true)}
                onMouseLeave={() => setShowNameToolTip(false)}
              >
                {file.name}
              </span>
            </Tooltip>

            <span className="text-gray-500 text-sm whitespace-nowrap">
              {isNumber(file.size) && Number(file.size) > 0
                ? `(${formatFileSize(Number(file.size))})`
                : ''}
            </span>
          </div>
          {file.status === EFileStatus.UPLOADING && (
            <Progress percent={file.percent} size="small" className="mt-2" />
          )}
          {file.status === EFileStatus.ERROR && (
            <Alert
              message="Upload failed"
              type="error"
              showIcon
              className="mt-2"
            />
          )}
        </div>
      </div>
      <div className="flex items-center space-x-2 ml-4">
        {file.status === EFileStatus.DONE && (
          <DownloadOutlined
            className="text-blue-600 cursor-pointer text-lg"
            onClick={() => handleDownload(file)}
          />
        )}
        {file.status === EFileStatus.ERROR && !uploading[file.uid] && (
          <ReloadOutlined
            className="text-blue-600 cursor-pointer text-lg"
            onClick={() => simulateUpload(file as CustomFile)}
          />
        )}
        {!disabled && (
          <DeleteOutlined
            className="text-red-600 cursor-pointer text-lg"
            onClick={() => handleRemove(file as CustomFile)}
          />
        )}
      </div>
    </div>
  );
};

export default FileItem;
